// @greenlight-spec: 28-it-dashboard-ui.md

export interface PemCertificateSummary {
  subject: string;
  issuer: string;
  notAfter: Date;
  /** SHA-256 fingerprint as colon-separated upper-case hex, the form `openssl` prints. */
  fingerprint: string;
}

export type PemParseResult =
  | { ok: true; certificates: PemCertificateSummary[] }
  | { ok: false; error: string };

const PEM_BLOCK = /-----BEGIN ([A-Z0-9 ]+)-----[\s\S]*?-----END \1-----/g;

/**
 * Lists the certificates in pasted or uploaded PEM so IT can see what it is
 * about to trust. The server re-checks everything on save; this mirrors its
 * refusals so the form can say why before submitting. The x509 library loads
 * only when PEM is entered.
 */
export async function parsePemCertificates(pem: string): Promise<PemParseResult> {
  if (/-----BEGIN [A-Z0-9 ]*PRIVATE KEY-----/.test(pem)) {
    return { ok: false, error: 'This contains a private key. Paste only certificates.' };
  }
  const blocks = [...pem.matchAll(PEM_BLOCK)];
  if (blocks.length === 0) {
    return { ok: false, error: 'No PEM certificate found. Paste one or more CERTIFICATE blocks.' };
  }
  const other = blocks.find((b) => b[1] !== 'CERTIFICATE');
  if (other) return { ok: false, error: `Only CERTIFICATE blocks are accepted, not ${other[1]}.` };
  if (blocks.length > 16) return { ok: false, error: 'At most 16 certificates can be trusted.' };

  const x509 = await import('@peculiar/x509');
  x509.cryptoProvider.set(globalThis.crypto);
  const certificates: PemCertificateSummary[] = [];
  for (const [i, block] of blocks.entries()) {
    try {
      const cert = new x509.X509Certificate(block[0]);
      const thumbprint = new Uint8Array(await cert.getThumbprint('SHA-256'));
      certificates.push({
        subject: cert.subject,
        issuer: cert.issuer,
        notAfter: cert.notAfter,
        fingerprint: Array.from(thumbprint, (b) => b.toString(16).padStart(2, '0'))
          .join(':')
          .toUpperCase(),
      });
    } catch {
      return { ok: false, error: `Certificate ${i + 1} is not a valid X.509 certificate.` };
    }
  }
  return { ok: true, certificates };
}
